import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';

const DashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('attorneyToken');

    if (!token) {
      navigate('/attorney/login');
      return;
    }

    loadDashboard(token);
  }, [navigate]);

  const loadDashboard = async (token) => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getAttorneyDashboard(token);
      setDashboardData(data);
    } catch (err) {
      setError(err.message || 'Failed to load dashboard');
      if (err.message.includes('Unauthorized') || err.message.includes('token')) {
        localStorage.removeItem('attorneyToken');
        navigate('/attorney/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('attorneyToken');
    navigate('/attorney/login');
  };

  const handleCaseClick = (caseId) => {
    navigate(`/attorney/case/${caseId}`);
  };

  const handleAnalyticsClick = () => {
    navigate('/attorney/analytics');
  };

  const getCheckpointLabel = (checkpoint) => {
    const labels = {
      1: 'Process Initiation',
      2: 'Party B Response',
      3: 'Settlement Approval'
    };
    return labels[checkpoint] || `Checkpoint ${checkpoint}`;
  };

  const getUrgencyBadge = (urgency) => {
    const styles = {
      high: 'bg-red-100 text-red-800 border-red-300',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      low: 'bg-blue-100 text-blue-800 border-blue-300'
    };
    return styles[urgency] || styles.low;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="card max-w-md">
          <div className="text-center">
            <div className="text-4xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold mb-2">Error Loading Dashboard</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { attorney, todayStats, allCases, reviewQueue } = dashboardData || {};

  // Map backend data to frontend expectations
  const stats = todayStats;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      draft: 'bg-gray-100 text-gray-800',
      responding: 'bg-blue-100 text-blue-800',
      negotiating: 'bg-yellow-100 text-yellow-800',
      settlement_proposed: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      abandoned: 'bg-red-100 text-red-800'
    };
    return statusMap[status] || 'bg-gray-100 text-gray-800';
  };

  const getReviewStatusBadgeClass = (reviewStatus) => {
    if (!reviewStatus || reviewStatus === 'pending') {
      return 'bg-yellow-100 text-yellow-800';
    }
    if (reviewStatus === 'approved') {
      return 'bg-green-100 text-green-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Attorney Dashboard</h1>
              {attorney && (
                <p className="text-sm text-gray-600 mt-1">
                  Welcome, {attorney.name || 'Attorney'}
                </p>
              )}
            </div>
            <button
              onClick={handleLogout}
              className="btn-secondary text-sm"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Today's Stats */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Today's Activity</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="card bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-800 mb-1">Reviews Completed</p>
                  <p className="text-3xl font-bold text-blue-900">
                    {stats?.reviewsCompleted || 0}
                  </p>
                </div>
                <div className="text-4xl">✓</div>
              </div>
            </div>

            <div className="card bg-gradient-to-br from-green-50 to-green-100 border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-800 mb-1">Cases Approved</p>
                  <p className="text-3xl font-bold text-green-900">
                    {stats?.approved || 0}
                  </p>
                </div>
                <div className="text-4xl">👍</div>
              </div>
            </div>

            <div className="card bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-800 mb-1">Modified</p>
                  <p className="text-3xl font-bold text-purple-900">
                    {stats?.modified || 0}
                  </p>
                </div>
                <div className="text-4xl">✏️</div>
              </div>
            </div>
          </div>
        </section>

        {/* All Cases List */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">All Cases</h2>
            <div className="flex gap-2">
              <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded text-sm font-medium">
                {reviewQueue?.pendingReview || 0} Pending Review
              </span>
              <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded text-sm font-medium">
                {allCases?.length || 0} Total
              </span>
            </div>
          </div>

          {allCases && allCases.length > 0 ? (
            <div className="card overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Case ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Claimant</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Respondent</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Review</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {allCases.map((caseItem) => (
                    <tr
                      key={caseItem.id}
                      className="hover:bg-gray-50 cursor-pointer transition"
                      onClick={() => handleCaseClick(caseItem.id)}
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">#{caseItem.id}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 capitalize">{caseItem.type}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{caseItem.initiator_name || caseItem.initiator_email || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{caseItem.responder_name || caseItem.responder_email || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{formatCurrency(caseItem.amount_claimed || 0)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${getStatusBadgeClass(caseItem.status)}`}>
                          {caseItem.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${getReviewStatusBadgeClass(caseItem.attorney_review_status)}`}>
                          {caseItem.attorney_review_status || 'Pending'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(caseItem.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="card text-center py-8">
              <p className="text-gray-600">No cases found</p>
            </div>
          )}
        </section>

        {/* Footer Actions */}
        <section className="mt-8">
          <div className="card bg-gradient-to-r from-primary-50 to-secondary-50 border border-primary-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-900 mb-1">View Analytics</h3>
                <p className="text-sm text-gray-600">
                  See detailed statistics and performance metrics
                </p>
              </div>
              <button
                onClick={handleAnalyticsClick}
                className="btn-primary"
              >
                Go to Analytics
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <p className="text-center text-sm text-gray-600">
            TensionBridge ADR - Attorney Portal
          </p>
        </div>
      </footer>
    </div>
  );
};

export default DashboardPage;
